/**
 * Teams Database References
 *
 * Teams and related collections:
 * - /clients/{clientKey}/teams/{teamId} (top-level teams)
 * - Teams can optionally link to organizations via organization_id field
 * - Team subcollections: documents, events, posts, videos, playbooks
 */

import {
  doc,
  collection,
  query,
  where,
  orderBy,
  limit,
  DocumentReference,
  CollectionReference,
  Query,
  DocumentData
} from 'firebase/firestore';
import { db } from '../../firebase';

/* ═══════════════════════════════════════════════════════════
   TEAMS COLLECTION (Top-Level)
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the teams collection
 * Path: /clients/{clientKey}/teams
 */
export const TeamsRef_Collection = (
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'teams');
};

/**
 * Reference to a specific team
 * Path: /clients/{clientKey}/teams/{teamId}
 */
export const TeamRef_Document = (
  teamId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'teams', teamId);
};

/**
 * Query for teams linked to a specific organization
 */
export const OrganizationTeamsRef_Query = (
  organizationId: string,
  clientKey: string = 'athleto',
  limitCount?: number
): Query<DocumentData> => {
  const baseQuery = query(
    TeamsRef_Collection(clientKey),
    where('organization_id', '==', organizationId),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for all teams
 */
export const AllTeamsRef_Query = (
  clientKey: string = 'athleto',
  limitCount?: number
): Query<DocumentData> => {
  const baseQuery = query(
    TeamsRef_Collection(clientKey),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for teams where user is a coach
 */
export const UserCoachTeamsRef_Query = (
  userId: string,
  clientKey: string = 'athleto',
  limitCount?: number
): Query<DocumentData> => {
  const baseQuery = query(
    TeamsRef_Collection(clientKey),
    where('coach_ids', 'array-contains', userId),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for teams where user is a member
 */
export const UserMemberTeamsRef_Query = (
  userId: string,
  clientKey: string = 'athleto',
  limitCount?: number
): Query<DocumentData> => {
  const baseQuery = query(
    TeamsRef_Collection(clientKey),
    where('members', 'array-contains', userId),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/* ═══════════════════════════════════════════════════════════
   TEAM EVENTS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the team events collection (top-level)
 * Path: /clients/{clientKey}/teamEvents
 */
export const TeamEventsRef_Collection = (
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'teamEvents');
};

/**
 * Reference to a specific team event
 * Path: /clients/{clientKey}/teamEvents/{eventId}
 */
export const TeamEventRef_Document = (
  eventId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'teamEvents', eventId);
};

/**
 * Query for team events by team
 */
export const TeamEventsRef_Query = (
  teamId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    TeamEventsRef_Collection(clientKey),
    where('team_id', '==', teamId),
    orderBy('event_date', 'asc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for upcoming team events
 */
export const UpcomingTeamEventsRef_Query = (
  teamId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const now = new Date();
  const baseQuery = query(
    TeamEventsRef_Collection(clientKey),
    where('team_id', '==', teamId),
    where('event_date', '>=', now),
    orderBy('event_date', 'asc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/* ═══════════════════════════════════════════════════════════
   TEAM POSTS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the team posts collection
 * Path: /clients/{clientKey}/teamPosts
 */
export const TeamPostsRef_Collection = (
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'teamPosts');
};

/**
 * Reference to a specific team post
 * Path: /clients/{clientKey}/teamPosts/{postId}
 */
export const TeamPostRef_Document = (
  postId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'teamPosts', postId);
};

/**
 * Query for posts by team
 */
export const TeamPostsRef_Query = (
  teamId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    TeamPostsRef_Collection(clientKey),
    where('team_id', '==', teamId),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Reference to a team post's comments collection
 * Path: /clients/{clientKey}/teamPosts/{postId}/comments
 */
export const TeamPostCommentsRef_Collection = (
  postId: string,
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'teamPosts', postId, 'comments');
};

/**
 * Reference to a specific comment on a team post
 * Path: /clients/{clientKey}/teamPosts/{postId}/comments/{commentId}
 */
export const TeamPostCommentRef_Document = (
  postId: string,
  commentId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'teamPosts', postId, 'comments', commentId);
};

/**
 * Query for a post's comments, oldest first
 */
export const TeamPostCommentsRef_Query = (
  postId: string,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  return query(
    TeamPostCommentsRef_Collection(postId, clientKey),
    orderBy('created_at', 'asc')
  );
};

/**
 * Reference to a team post's reactions collection
 * Path: /clients/{clientKey}/teamPosts/{postId}/reactions
 * Note: reaction doc ID is the reacting user's ID (enforced by security rules)
 */
export const TeamPostReactionsRef_Collection = (
  postId: string,
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'teamPosts', postId, 'reactions');
};

/**
 * Reference to a user's reaction on a team post
 * Path: /clients/{clientKey}/teamPosts/{postId}/reactions/{userId}
 */
export const TeamPostReactionRef_Document = (
  postId: string,
  userId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'teamPosts', postId, 'reactions', userId);
};

/* ═══════════════════════════════════════════════════════════
   TEAM JOIN REQUESTS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the team join requests collection
 * Path: /clients/{clientKey}/teamJoinRequests
 */
export const TeamJoinRequestsRef_Collection = (
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'teamJoinRequests');
};

/**
 * Reference to a specific team join request
 * Path: /clients/{clientKey}/teamJoinRequests/{requestId}
 */
export const TeamJoinRequestRef_Document = (
  requestId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'teamJoinRequests', requestId);
};

/**
 * Query for join requests by team
 */
export const TeamJoinRequestsRef_Query = (
  teamId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    TeamJoinRequestsRef_Collection(clientKey),
    where('team_id', '==', teamId),
    orderBy('requested_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for join requests by user
 */
export const UserTeamJoinRequestsRef_Query = (
  userId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    TeamJoinRequestsRef_Collection(clientKey),
    where('user_id', '==', userId),
    orderBy('requested_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/* ═══════════════════════════════════════════════════════════
   TEAM DOCUMENT CATEGORIES COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to team document categories collection
 * Path: /clients/{clientKey}/teams/{teamId}/document_categories
 */
export const TeamDocumentCategoriesRef_Collection = (
  teamId: string,
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'teams', teamId, 'document_categories');
};

/**
 * Reference to a specific team document category
 * Path: /clients/{clientKey}/teams/{teamId}/document_categories/{categoryId}
 */
export const TeamDocumentCategoryRef_Document = (
  teamId: string,
  categoryId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'teams', teamId, 'document_categories', categoryId);
};

/* ═══════════════════════════════════════════════════════════
   TEAM DOCUMENT REQUIREMENTS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to team document requirements collection
 * Path: /clients/{clientKey}/teams/{teamId}/document_requirements
 */
export const TeamDocumentRequirementsRef_Collection = (
  teamId: string,
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'teams', teamId, 'document_requirements');
};

/**
 * Reference to a specific team document requirement
 * Path: /clients/{clientKey}/teams/{teamId}/document_requirements/{requirementId}
 */
export const TeamDocumentRequirementRef_Document = (
  teamId: string,
  requirementId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'teams', teamId, 'document_requirements', requirementId);
};

/**
 * Query for document requirements by category
 */
export const TeamDocumentRequirementsByCategoryRef_Query = (
  teamId: string,
  categoryId: string | null,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  return query(
    TeamDocumentRequirementsRef_Collection(teamId, clientKey),
    where('category_id', '==', categoryId),
    orderBy('created_at', 'desc')
  );
};

/* ═══════════════════════════════════════════════════════════
   TEAM DOCUMENT UPLOADS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to team document uploads collection
 * Path: /clients/{clientKey}/teams/{teamId}/document_uploads
 */
export const TeamDocumentUploadsRef_Collection = (
  teamId: string,
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'teams', teamId, 'document_uploads');
};

/**
 * Reference to a specific team document upload
 * Path: /clients/{clientKey}/teams/{teamId}/document_uploads/{uploadId}
 */
export const TeamDocumentUploadRef_Document = (
  teamId: string,
  uploadId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'teams', teamId, 'document_uploads', uploadId);
};

/**
 * Query for document uploads by requirement
 */
export const TeamDocumentUploadsByRequirementRef_Query = (
  teamId: string,
  requirementId: string,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  return query(
    TeamDocumentUploadsRef_Collection(teamId, clientKey),
    where('requirement_id', '==', requirementId),
    orderBy('uploaded_at', 'desc')
  );
};

/**
 * Query for document uploads by user
 */
export const TeamDocumentUploadsByUserRef_Query = (
  teamId: string,
  userId: string,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  return query(
    TeamDocumentUploadsRef_Collection(teamId, clientKey),
    where('user_id', '==', userId),
    orderBy('uploaded_at', 'desc')
  );
};
